import { HttpErrorResponse } from '@angular/common/http';
import { Component, inject, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { PaymentCardFormComponent } from '@components/consultation-request/payment-card-form/payment-card-form.component';
import { DataLayerCategories } from '@enums/data-layer-categories';
import { PaymentTypes } from '@enums/payment-types';
import { ConfigService as PaymentConfigService, Payment } from '@medology/ngx-payment';
import { LegacyUpsellStoreResponse } from '@models/legacy-upsell-store-response.model';
import { OrderUpsell } from '@models/order-upsell';
import { LegacyUpsellStoreRequest } from '@models/requests/legacy-upsell-store.model';
import { Upsell } from '@models/upsell';
import { DataLayerService } from '@services/data-layer.service';
import { ErrorHandlerService } from '@services/error-handler.service';
import { LoadingService } from '@services/loading.service';
import { NavigationService } from '@services/navigation.service';
import { OrderService } from '@services/order.service';
import { PaymentService } from '@services/payment.service';
import { StorageService } from '@services/storage.service';

@Component({
  template: '',
})
export abstract class OrderFlowUpsellComponent {
  @ViewChild('paymentCardForm', { static: false }) public paymentCardFormComponent: PaymentCardFormComponent;

  public submissionErrors: string[] = [];
  public processing: boolean = false;
  public showPaymentInformation: boolean = false;
  public rechargeAttempt: boolean = false;
  public payments: Payment[] = [];

  protected paymentConfigService = inject(PaymentConfigService);
  protected loadingService = inject(LoadingService);
  protected router = inject(Router);
  protected storageService = inject(StorageService);
  protected orderService = inject(OrderService);
  protected errorHandlerService = inject(ErrorHandlerService);
  protected dataLayerService = inject(DataLayerService);
  protected navigationService = inject(NavigationService);
  protected paymentService = inject(PaymentService);

  public abstract readonly upsellTest: OrderUpsell;

  public constructor() {
    this.paymentService.setGiftCardConfig(this.paymentConfigService);
  }

  /**
   * Navigates to the next page in the order flow.
   */
  public navigateToNextPage(): void {
    this.router.navigateByUrl(this.navigationService.getNextNavigationUrl());
  }

  /**
   * Handles payment selection from the payment form.
   *
   * @param {Payment[]} payments the selected payments
   */
  public onPaymentsSelected(payments: Payment[]): void {
    this.payments = payments;
    this.submissionErrors = [];
  }

  /**
   * Handles the upsell submission flow, delegating to recharge or payment based on the current state.
   */
  public submit(): void {
    this.submissionErrors = [];
    this.processing = true;

    if (this.rechargeAttempt) {
      this.submitPayment();

      return;
    }

    this.rechargeAttempt = true;

    if (this.storageService.paymentType === PaymentTypes.PayLater) {
      this.showPaymentInformation = true;
      this.processing = false;

      return;
    }

    this.submitRecharge();
  }

  /**
   * Handles a successful upsell store response, updating storage and triggering tracking.
   *
   * @param { LegacyUpsellStoreResponse } upsellStoreResponse the response returned by the upsell store endpoint
   */
  protected handleSuccessfulStoreUpsell(upsellStoreResponse: LegacyUpsellStoreResponse): void {
    this.storageService.paymentType = PaymentTypes.Recharge;
    this.orderService.addUpsellTestToStorage(this.upsellTest);
    this.orderService.addUpsellPriceToTotal(this.upsellTest.upsell_price);

    if (upsellStoreResponse.transaction_id) {
      const upsellTransactionIds = this.storageService.upsell;

      upsellTransactionIds.push(upsellStoreResponse.transaction_id);
      this.storageService.upsell = upsellTransactionIds;
    }

    this.dataLayerService.pushDataLayerTracking(DataLayerCategories.Upsell);
    this.onUpsellSuccess();
  }

  /**
   * Handles an HTTP error from an upsell request, optionally populating submission errors.
   *
   * @param {HttpErrorResponse} error the HTTP error response from the failed request
   * @param {boolean} setSubmissionErrors whether to populate submission errors from the response
   */
  protected handleRequestError(error: HttpErrorResponse, setSubmissionErrors: boolean = true): void {
    this.processing = false;
    this.submissionErrors = setSubmissionErrors ? this.errorHandlerService.handleResponseError(error) : [];
  }

  /**
   * Submits the upsell using the stored recharge payment method.
   */
  private submitRecharge(): void {
    this.orderService
      .legacyStoreUpsell(new LegacyUpsellStoreRequest(new Upsell(this.upsellTest.name), PaymentTypes.Recharge))
      .subscribe({
        next: this.handleSuccessfulStoreUpsell.bind(this),
        error: (error) => {
          this.showPaymentInformation = true;
          this.handleRequestError(error, false);
        },
      });
  }

  /**
   * Validates the payment card form and submits the upsell using the selected payment methods.
   */
  private submitPayment(): void {
    if (!this.paymentCardFormComponent?.validateForm()) {
      this.processing = false;

      return;
    }

    this.orderService
      .legacyStoreUpsell(
        LegacyUpsellStoreRequest.fromPayments(
          new Upsell(this.upsellTest.name),
          [
            this.paymentService.getPrimaryPayment(this.payments),
            this.paymentService.getGiftCardAsSecondaryPayment(this.payments),
          ],
          this.storageService.patient.name,
        ),
      )
      .subscribe({
        next: this.handleSuccessfulStoreUpsell.bind(this),
        error: (error) => {
          this.processing = false;
          this.handleRequestError(error);
        },
      });
  }

  /**
   * Called after a successful upsell store. Subclasses define the post-upsell navigation or side effects.
   */
  protected abstract onUpsellSuccess(): void;
}
